import { useCallback, useEffect, useMemo, useState, type FC } from "react";
import {
  deleteCertificatePolicy,
  useCertificatePolicies,
} from "../stores/certificate_policy_store";
import { useDeviceGroups } from "../stores/device_group_store";
import { useCertificates } from "../stores/certificate_store";
import type { CertificatePolicy } from "../../../shared/models/certificate_policy";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Pagination } from "../components/pagination";
import { useActiveCertCountMap } from "../lib/active_cert_counts";
import { modalManager } from "../lib/modal_store";
import { useCustomerId } from "../stores/customer_id_store";
import { UnassignedPlaceholder } from "../components/unassigned_placeholder";

export const CertificatePolicies: FC = () => {
  const customerId = useCustomerId();
  const policies = useCertificatePolicies();
  const groups = useDeviceGroups();
  const policyIdsInUse = useMemo(
    () => new Set(groups.map((g) => g.certificatePolicyId)),
    [groups]
  );
  const deviceGroupCountByPolicy = useMemo(() => {
    const map = new Map<string, number>();
    for (const g of groups) {
      map.set(g.certificatePolicyId, (map.get(g.certificatePolicyId) ?? 0) + 1);
    }
    return map;
  }, [groups]);
  const sortedPolicies = useMemo(
    () =>
      [...policies].sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      ),
    [policies]
  );
  const certificates = useCertificates();
  const policyKeyFn = useCallback(
    (c: { certificatePolicyId: string }) => c.certificatePolicyId,
    []
  );
  const certCountByPolicy = useActiveCertCountMap(certificates, policyKeyFn);

  // Total cert count (all statuses) — used to prevent deletion
  const totalCertCountByPolicy = useMemo(() => {
    const map = new Map<string, number>();
    for (const c of certificates) {
      map.set(c.certificatePolicyId, (map.get(c.certificatePolicyId) ?? 0) + 1);
    }
    return map;
  }, [certificates]);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const totalItems = sortedPolicies.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const paginatedPolicies = sortedPolicies.slice(
    (safePage - 1) * pageSize,
    safePage * pageSize
  );

  const openCreate = (source?: CertificatePolicy) => {
    modalManager.open("createPolicy", { duplicateFrom: source });
    // Clean the action param
    if (searchParams.has("action")) {
      searchParams.delete("action");
      setSearchParams(searchParams, { replace: true });
    }
  };

  // Handle ?action=create on mount
  useEffect(() => {
    if (searchParams.get("action") === "create") {
      openCreate();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!customerId) {
    return <UnassignedPlaceholder />;
  }

  return (
    <div className="vmx-page">
      <div className="vmx-page-header">
        <div>
          <div className="vmx-page-title">⛨ Certificate Policies</div>
          <div className="vmx-page-subtitle">
            Define authentication rules, key parameters, and certificate
            lifetimes for your device groups.
          </div>
        </div>
        <div className="vmx-page-actions">
          <button
            className="vmx-btn vmx-btn-primary"
            onClick={() => openCreate()}
          >
            ＋ New Certificate Policy
          </button>
        </div>
      </div>

      {sortedPolicies.length === 0 ? (
        <div className="vmx-card">
          <div className="vmx-empty">
            <div className="vmx-empty-icon">⛨</div>
            <div className="vmx-empty-title">No certificate policies yet</div>
            <div className="vmx-empty-text">
              Create your first certificate policy to define how devices
              authenticate.
            </div>
            <button
              className="vmx-btn vmx-btn-primary"
              onClick={() => openCreate()}
            >
              ＋ Create Certificate Policy
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="vmx-table-wrapper vmx-card-table-wrapper vmx-policies-table-wrapper">
            <table className="vmx-table vmx-card-table vmx-policies-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Device Groups</th>
                  <th>Certificates</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedPolicies.map((p) => (
                  <tr
                    key={p.id}
                    onClick={() =>
                      modalManager.open("policyDetail", { policy: p })
                    }
                    style={{ cursor: "pointer" }}
                  >
                    <td data-label="Name">
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                        }}
                      >
                        <span
                          style={{
                            fontWeight: 600,
                            color: "var(--vmx-gray-800)",
                          }}
                        >
                          {p.name}
                        </span>
                        {p.isDefault && (
                          <span className="vmx-badge vmx-badge-neutral">
                            Default
                          </span>
                        )}
                      </div>
                    </td>
                    <td data-label="Device Groups">
                      <span
                        style={{
                          fontWeight: 600,
                          color: "var(--vmx-gray-700)",
                          padding: "2px 6px",
                        }}
                      >
                        {deviceGroupCountByPolicy.get(p.id) ?? 0}
                      </span>
                    </td>
                    <td data-label="Certificates">
                      <button
                        type="button"
                        className="vmx-btn vmx-btn-ghost vmx-btn-sm"
                        style={{
                          color: "var(--vmx-accent)",
                          fontWeight: 600,
                          padding: "2px 6px",
                        }}
                        onClick={(event) => {
                          event.stopPropagation();
                          navigate(`/certificates?policyId=${p.id}`);
                        }}
                      >
                        {certCountByPolicy.get(p.id) ?? 0}
                      </button>
                    </td>
                    <td data-label="Actions">
                      <div style={{ display: "flex", gap: 6 }}>
                        <button
                          className="vmx-btn vmx-btn-ghost vmx-btn-sm"
                          title="Duplicate"
                          onClick={(event) => {
                            event.stopPropagation();
                            openCreate(p);
                          }}
                        >
                          ⧉ Duplicate
                        </button>
                        {!p.isDefault &&
                          !policyIdsInUse.has(p.id) &&
                          (totalCertCountByPolicy.get(p.id) ?? 0) === 0 && (
                            <button
                              className="vmx-btn vmx-btn-ghost vmx-btn-sm vmx-delete-policy-btn"
                              title="Delete"
                              style={{ color: "var(--vmx-danger)" }}
                              onClick={(event) => {
                                event.stopPropagation();
                                modalManager.open("deleteConfirm", {
                                  title: "Delete Certificate Policy",
                                  message: (
                                    <p>
                                      Are you sure you want to delete{" "}
                                      <strong>{p.name}</strong>? This action
                                      cannot be undone.
                                    </p>
                                  ),
                                  onConfirm: async () => {
                                    await deleteCertificatePolicy(p.id);
                                  },
                                });
                              }}
                            >
                              <span className="vmx-delete-icon">✕</span>
                              <span className="vmx-delete-label">Delete</span>
                            </button>
                          )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination
            currentPage={safePage}
            totalItems={totalItems}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setCurrentPage(1);
            }}
          />
        </>
      )}
    </div>
  );
};
